import type {
  MedicalAidPerson,
  MedicalAidValidationResponse,
  PersonContact,
} from '@quromedical/models';
import {
  Alert,
  Button,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import type { FormikErrors } from 'formik';
import { type ErrorMessageType, getErrorMessage } from 'helpers';
import {
  getPlaceAddressPartialFetcher,
  googleSearchAddressFetcher,
} from 'integration';
import type React from 'react';
import { useCallback, useState } from 'react';
import type { JointFields } from 'screens/patient';
import { strings } from 'strings';
import { trpcClient } from 'trpc';

import { NextOfKinForm } from '../forms';
import { NextOfKinSelectionCard } from './NextOfKinSelectionCard';
import { isAdult } from './PatientStep';

export type PatientStepData = {
  results: MedicalAidValidationResponse;
  patient: MedicalAidPerson;
  onSubmit: (nextOfKin: JointFields) => void;
};

/**
 * Get options that are allowed as next-of-kin
 */
const getNextOfKinOptions = (
  results: MedicalAidValidationResponse,
  patient: Partial<MedicalAidPerson>,
): Partial<MedicalAidPerson>[] =>
  results
    // only members that are eligible for next of kin
    .filter((result) => isAdult(result))
    // remove the input patient from the list
    .filter(
      (result) =>
        result.medicalAidMembership?.dependantCode !==
        patient.medicalAidMembership?.dependantCode,
    );

export const NextOfKinStep: React.FC<PatientStepData> = ({
  results,
  onSubmit,
  patient,
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [nextOfKinError, setNextOfKinError] = useState(false);
  const [selected, setSelected] = useState<Partial<MedicalAidPerson>>();
  const [nextOfKin, setNexOfKin] = useState<Partial<JointFields>>();
  const [loading, setLoading] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [showAlertMessage, setShowAlertMessage] = useState<ErrorMessageType>();

  const options = getNextOfKinOptions(results, patient);
  const onNextOfKinChange = useCallback(
    (data: Partial<JointFields>, err: FormikErrors<Partial<PersonContact>>) => {
      const hasErrors = !!Object.keys(err).length;
      setNexOfKin({ ...nextOfKin, ...data });
      setNextOfKinError(hasErrors);
    },
    [nextOfKin],
  );

  const onSelectionChange = useCallback(
    async (result?: Partial<MedicalAidPerson>) => {
      try {
        setLoading(true);
        setSelected(result);

        const identifierValue = result?.general?.identifierValue;
        if (!identifierValue) {
          setLoading(false);
          return setNexOfKin({});
        }
        const results = await trpcClient.patient.getAll.query({
          identifier: result?.general?.identifierValue,
          count: 1,
        });

        if (results.patients.length) {
          setNexOfKin({ ...results.patients[0].contact });
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
        setShowAlert(true);
        setShowAlertMessage(getErrorMessage(error));
      }
    },
    [],
  );

  const showErrors = submitted && nextOfKinError;

  const handleSubmit = useCallback(() => {
    const merged: Partial<JointFields> = {
      ...nextOfKin,
    };
    setSubmitted(true);
    if (!nextOfKinError) {
      onSubmit(merged as JointFields);
    }
  }, [nextOfKin, nextOfKinError, onSubmit]);

  const initialFormData = {
    ...(selected?.contact || {}),
    ...(selected?.general || {}),
  };

  const onAlertClose = useCallback(() => {
    setShowAlert(false);
  }, []);

  const initial = { ...nextOfKin, ...initialFormData };

  return (
    <SkeletonProvider loading={loading}>
      <Section>
        <NextOfKinSelectionCard
          options={options}
          onSelect={onSelectionChange}
        />
      </Section>

      <Section>
        <Skeleton>
          <NextOfKinForm
            key={`${initialFormData.identifierCode}`}
            initial={initial}
            showErrors={showErrors}
            onChange={onNextOfKinChange}
            addressFetcher={googleSearchAddressFetcher}
            getAddressPartial={getPlaceAddressPartialFetcher}
          />
        </Skeleton>
      </Section>

      <Section alignItems="flex-end" hasBottomMargin>
        <Button onPress={handleSubmit} text={strings.ButtonTextSubmit} />
      </Section>
      <Snackbar isOpen={showAlert} onClose={onAlertClose}>
        <Alert
          actionIcon="close"
          backgroundColor="status-critical"
          onAction={onAlertClose}
          title={strings.AlertError}
          body={(showAlertMessage as string) || strings.GenericErrorMessage}
          icon="error"
        />
      </Snackbar>
    </SkeletonProvider>
  );
};
